/** Settings-only planner for SourceSceneSpecFields. Never execute providers or mutate a capture. */
import type { EditorState } from '../../vendor/src/features/remotion-editor/editor/state/types';
import { copyEditorJson, editorValueSignature, type NativeWorkScope } from '../../protocol/native-envelope';
import { readSceneGraph, sceneId, sceneRecord, sceneScopeMatches, validateSceneSpecTarget, validCurrentSceneSpecReferences,
  type NativeSceneSpec, type SceneGenSpec } from '../../protocol/source-scene-generation';
import { createSceneSpecFieldConverter, sceneSpecFormFromSpec, type SceneSpecFormValue } from '../../../../src/lib/agent/scene-gen-spec-core';
import { hasPendingTimelineEdit } from './edit-ownership';
import { readSourceSceneSpec, writeSourceSceneSpecState, type NativeSceneSpecHelpers } from './source-scene-spec';
import type { SourceSceneSpecDraftPatch } from './source-scene-spec-draft';

export type SourceSceneSpecFieldsSavePlan = {
  state: EditorState;
  saved: NativeSceneSpec;
  expectedUndoableState: EditorState['undoableState'];
  durationIssue: string | null;
  droppedKeys: string[];
};
function checkForm(form: SceneSpecFormValue) {
  if (!sceneRecord(form) || !['action', 'extraRequest', 'startAnchorCastName', 'ambientSound', 'sfxNote'].every(key => typeof form[key] === 'string')
    || !['', 'prev-end', 'character-main', 'upload', 'none'].includes(form.startAnchorKind)
    || typeof form.captionsInClip !== 'boolean' || (form.durationSec !== null && typeof form.durationSec !== 'number'))
    throw Error('씬 설정 입력을 다시 확인해 주세요.');
}
/** Parent commits exactly plan.state against plan.expectedUndoableState in one CAS/Undo,
 * then recaptures the target and returns the actual committed echo to the fields. */
export async function planSourceSceneSpecFieldsSave(state: EditorState, patch: SourceSceneSpecDraftPatch,
  authority: { scope: NativeWorkScope; revision: string }, helpers: NativeSceneSpecHelpers): Promise<SourceSceneSpecFieldsSavePlan> {
  const expectedUndoableState = state.undoableState;
  if (patch.documentIdentity !== expectedUndoableState || hasPendingTimelineEdit(state))
    throw Error('편집 자료가 바뀌었거나 이동 중입니다. 씬을 다시 선택해 주세요.');
  // Freeze submitted values before the async signature check. NaN duration stays a partial-save input.
  const submitted = structuredClone({ target: patch.target, saved: patch.saved, form: patch.form,
    referenceSelections: patch.referenceSelections, capsuleBase: patch.capsuleBase, seedEdit: patch.seedEdit });
  checkForm(submitted.form);
  if (!sceneRecord(submitted.referenceSelections) || Object.entries(submitted.referenceSelections).some(([key, value]) =>
    !['start', 'location', 'ai', 'consistency'].includes(key) || (value !== null && !sceneId(value))))
    throw Error('연결할 이미지를 현재 자료함에서 다시 선택해 주세요.');
  const originalSignature = editorValueSignature(expectedUndoableState);
  const captured = copyEditorJson(expectedUndoableState);
  await validateSceneSpecTarget(submitted.target, captured, authority.scope, authority.revision);
  if (state.undoableState !== expectedUndoableState || editorValueSignature(expectedUndoableState) !== originalSignature || hasPendingTimelineEdit(state)
    || submitted.target.revision !== authority.revision || !sceneScopeMatches(submitted.target.scope, authority.scope))
    throw Error('검증 중 편집 자료가 바뀌었습니다. 씬을 다시 선택해 주세요.');
  const graph = readSceneGraph(captured), saved = readSourceSceneSpec(captured, submitted.target, helpers);
  if (editorValueSignature(saved) !== editorValueSignature(submitted.saved))
    throw Error('저장된 씬 설정이 바뀌었습니다. 다시 선택해 주세요.');
  const base: SceneGenSpec = structuredClone(saved.capsule ?? {});
  const inheritedConsistency = { ...saved.regenFields?.consistency, ...base.consistency };
  if (Object.keys(inheritedConsistency).length) base.consistency = inheritedConsistency;
  for (const slot of ['start', 'location', 'ai', 'consistency'] as const) {
    const id = submitted.referenceSelections[slot];
    if (id === undefined) continue;
    const asset = id === null ? undefined : graph.assets[id];
    if (id !== null) {
      if (!asset || asset.id !== id || asset.type !== 'image' || typeof asset.remoteUrl !== 'string')
        throw Error('연결할 이미지를 현재 자료함에서 다시 선택해 주세요.');
      const url = new URL(asset.remoteUrl);
      if (url.protocol !== 'https:' || url.username || url.password)
        throw Error('자료함에 업로드된 이미지를 선택해 주세요.');
    }
    if (slot === 'start') {
      if (asset) base.startAnchor = { kind: 'upload', url: String(asset.remoteUrl) };
      else if (base.startAnchor?.kind === 'upload') delete base.startAnchor;
    } else if (slot === 'consistency') {
      const consistency = { ...base.consistency };
      if (asset) consistency.anchorUrl = String(asset.remoteUrl);
      else delete consistency.anchorUrl;
      if (Object.keys(consistency).length) base.consistency = consistency;
      else delete base.consistency;
    } else {
      const key = slot === 'location' ? 'locationRef' : 'aiRef';
      if (asset) {
        const label = typeof asset.label === 'string' && asset.label ? asset.label
          : typeof asset.filename === 'string' ? asset.filename : undefined;
        base[key] = { url: String(asset.remoteUrl), ...(label ? { label } : {}) };
      } else delete base[key];
    }
  }
  // capsuleBase carries the UI's only otherwise-unexpressed control: seed edit/unset.
  // All other capsule differences must equal the ID-resolved reference changes above.
  if (submitted.seedEdit !== undefined && typeof submitted.seedEdit !== 'string')
    throw Error('일관성 번호를 다시 확인해 주세요.');
  const seed = submitted.seedEdit === undefined ? base.consistency?.seed
    : submitted.seedEdit.trim() === '' ? undefined : Number(submitted.seedEdit);
  if (submitted.seedEdit !== undefined && submitted.seedEdit.trim() !== '' && !/^\d+$/.test(submitted.seedEdit.trim()))
    throw Error('일관성 번호를 유효한 정수로 입력하거나 비워 주세요.');
  if (seed !== undefined && helpers.preserveSceneGenSpec({ consistency: { seed } }).spec?.consistency?.seed !== seed)
    throw Error('일관성 번호를 유효한 정수로 입력하거나 비워 주세요.');
  const consistency = { ...base.consistency };
  if (seed === undefined) delete consistency.seed;
  else consistency.seed = seed;
  if (Object.keys(consistency).length) base.consistency = consistency;
  else delete base.consistency;
  const expectedBase = helpers.preserveSceneGenSpec(base);
  if (expectedBase.droppedKeys.length || editorValueSignature(expectedBase.spec ?? null) !== editorValueSignature(submitted.capsuleBase ?? null))
    throw Error('참조나 일관성 번호 외의 숨겨진 씬 설정은 변경할 수 없습니다. 다시 선택해 주세요.');
  if (submitted.form.startAnchorKind === 'upload' && !base.startAnchor?.url)
    throw Error('시작 이미지 한 장을 자료함에서 선택해 주세요.');
  const bounds = helpers.sceneSpecDurationBounds(saved.durationContext ?? {});
  const duration = submitted.form.durationSec;
  const durationIssue = duration !== null && (!Number.isFinite(duration) || duration <= 0)
    ? '길이는 양수로 입력하거나 자동으로 바꿔 주세요.' : helpers.sceneSpecDurationBlocked(duration ?? undefined, bounds);
  const form = durationIssue ? { ...submitted.form, durationSec: sceneSpecFormFromSpec(saved.capsule).durationSec } : submitted.form;
  // The baseline describes the authoritative pre-edit capsule, never the draft's new references/seed.
  const baseline = helpers.baselineStampSceneGenSpec(saved.capsule);
  const stampedBase = { ...expectedBase.spec, ...(baseline?.stamp ? { stamp: baseline.stamp } : {}) };
  const preserved = helpers.preserveSceneGenSpec(createSceneSpecFieldConverter(helpers.preserveSceneGenSpec)(form, stampedBase));
  const next: NativeSceneSpec = { ...saved, capsule: preserved.spec };
  // Native fields are execution authority. Keep edits/removals in both native representations;
  // absent seed/anchor must not inherit old producer values on the next request or reopen.
  const regenFields = { ...(saved.regenFields ?? { prompt: '' }) };
  if (preserved.spec?.consistency) regenFields.consistency = structuredClone(preserved.spec.consistency);
  else delete regenFields.consistency;
  if (saved.regenFields || regenFields.consistency) next.regenFields = regenFields;
  if (!next.capsule) delete next.capsule;
  if (!validCurrentSceneSpecReferences(next, graph))
    throw Error('설정에 연결된 이미지나 등장인물의 기준 이미지를 현재 자료함에서 다시 지정해 주세요.');
  const resultState = writeSourceSceneSpecState(state, submitted.target, next, helpers);
  const unchanged = editorValueSignature(resultState.undoableState) === originalSignature;
  return { state: unchanged ? state : resultState, saved: next, expectedUndoableState, durationIssue, droppedKeys: preserved.droppedKeys };
}
